import type { VoiceCard } from '../api/voiceCatalog'

/**
 * Подписи и оформление карточки голоса. Слова согласуются со словом «голос»: «мужской · взрослый · русский».
 */

const GENDER_WORDS = { m: 'мужской', f: 'женский' } as const
const AGE_WORDS = { young: 'молодой', adult: 'взрослый', old: 'пожилой' } as const
const LANGUAGE_WORDS: Record<string, string> = {
	ru: 'русский', en: 'английский', uk: 'украинский', de: 'немецкий', fr: 'французский', es: 'испанский',
	it: 'итальянский', pt: 'португальский', pl: 'польский', tr: 'турецкий', ja: 'японский', zh: 'китайский',
	ko: 'корейский', ar: 'арабский', hi: 'хинди',
}

/** «Мужской · взрослый · русский»: что известно о голосе, по порядку. Пусто — ничего не известно. */
export function voiceMetaLine(v: Pick<VoiceCard, 'gender' | 'age' | 'language' | 'russian'>): string {
	const parts: string[] = []
	if (v.gender) parts.push(GENDER_WORDS[v.gender])
	if (v.age) parts.push(AGE_WORDS[v.age])
	const lang = v.russian ? 'русский' : v.language ? LANGUAGE_WORDS[v.language] : undefined
	if (lang) parts.push(lang)
	const line = parts.join(' · ')
	return line ? line[0].toUpperCase() + line.slice(1) : ''
}

/** Устойчивое число из id: один голос — всегда одна обложка и одна «волна». */
export function voiceSeed(id: string): number {
	let h = 2166136261
	for (let i = 0; i < id.length; i++) {
		h ^= id.charCodeAt(i)
		h = Math.imul(h, 16777619)
	}
	return h >>> 0
}

/** Высоты столбиков «волны» (0.22…1) — рисунок голоса, а не замер звука: одинаковый при каждом открытии. */
export function voiceWave(id: string, bars = 26): number[] {
	let s = voiceSeed(id) || 1
	const out: number[] = []
	for (let i = 0; i < bars; i++) {
		s ^= s << 13
		s ^= s >>> 17
		s ^= s << 5
		const r = ((s >>> 0) % 1000) / 1000
		// Огибающая: к краям тише, в середине выше — похоже на фразу, а не на шум.
		const env = 0.55 + 0.45 * Math.sin(Math.PI * (i + 0.5) / bars)
		out.push(Math.round((0.22 + 0.78 * r * env) * 100) / 100)
	}
	return out
}

/** Обложка голоса: два оттенка из id — у каждого голоса свой цвет, как у плиток Artlist. */
export function voiceCover(id: string): string {
	const seed = voiceSeed(id)
	const hue = seed % 360
	const hue2 = (hue + 40 + (seed >>> 9) % 80) % 360
	return `radial-gradient(120% 90% at 20% 15%, hsl(${hue} 70% 62%) 0%, hsl(${hue2} 65% 38%) 55%, hsl(${hue2} 60% 14%) 100%)`
}

/** Подсказка пилюли голоса (08.10): кому принадлежит голос, его имя и короткая справка; есть ли проба. */
export function voiceTip(voice: VoiceCard, characterName?: string | null): string {
	const meta = [voice.hint, voiceMetaLine(voice)].filter(Boolean).join(' · ')
	const who = characterName ? `Голос: ${characterName} — ${voice.name}` : `Голос «${voice.name}»`
	return `${who}${meta ? ` · ${meta}` : ''}${voice.preview_url ? '. Нажмите, чтобы прослушать' : '. Пробы нет'}`
}
